<div class="cv2-page">

  <!-- ── Info banner ─────────────────────────────────────────────────── -->
  <div class="org-banner" *ngIf="bannerVisible">
    <mat-icon class="org-banner-icon">info_outline</mat-icon>
    <div class="org-banner-text">
      <strong>Classes</strong>
      <p>
        Classes represent confirmed training engagements with allocated cloud resources.
        Each class tracks its lifecycle from pending through to completion,
        with dedicated subscriptions, schedules, and participant lists.
      </p>
    </div>
    <div class="org-banner-actions">
      <mat-checkbox [(ngModel)]="bannerDismissForever" class="banner-dismiss-check">
        Don't show again
      </mat-checkbox>
      <button mat-icon-button class="banner-close-btn"
              (click)="dismissBanner()"
              matTooltip="Dismiss">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <!-- ── Toolbar ─────────────────────────────────────────────────── -->
  <div class="cv2-toolbar">
    <div class="cv2-toolbar-left">
      <mat-form-field appearance="outline" class="search-field" subscriptSizing="dynamic">
        <mat-label>Search classes…</mat-label>
        <mat-icon matPrefix>search</mat-icon>
        <input
          matInput
          [(ngModel)]="searchQuery"
          (ngModelChange)="onSearchChange()"
          placeholder="Name, trainer, course, date"
          autocomplete="off">
        <button
          *ngIf="searchQuery"
          mat-icon-button
          matSuffix
          (click)="clearSearch()"
          matTooltip="Clear search"
          aria-label="Clear search">
          <mat-icon>close</mat-icon>
        </button>
      </mat-form-field>
    </div>

    <div class="cv2-toolbar-right">
      <mat-button-toggle-group
        [value]="viewMode"
        (change)="onViewModeChange($event.value)"
        aria-label="View mode">
        <mat-button-toggle value="board" matTooltip="Board view" aria-label="Board view">
          <mat-icon>view_kanban</mat-icon>
        </mat-button-toggle>
        <mat-button-toggle value="table" matTooltip="Table view" aria-label="Table view">
          <mat-icon>table_rows</mat-icon>
        </mat-button-toggle>
      </mat-button-toggle-group>
    </div>
  </div>

  <!-- ── Loading ─────────────────────────────────────────────────── -->
  <div class="cv2-loading" *ngIf="isLoading">
    <mat-progress-spinner mode="indeterminate" diameter="44"></mat-progress-spinner>
    <p>Loading classes…</p>
  </div>

  <!-- ── Board View ──────────────────────────────────────────────── -->
  <div class="cv2-board" *ngIf="!isLoading && viewMode === 'board'" role="region" aria-label="Classes board">

    <!-- Pending Lane -->
    <div class="lane" role="list" aria-label="Pending classes">
      <div class="lane-header lane-header-pending">
        <div class="lane-header-left">
          <span class="lane-title">Pending</span>
          <span class="lane-count" [attr.aria-label]="pendingClasses.length + ' pending'">{{ pendingClasses.length }}</span>
        </div>
        <button mat-icon-button (click)="toggleLaneSort('pending')" [matTooltip]="sortDir['pending'] === 'desc' ? 'Sort ascending' : 'Sort descending'" aria-label="Toggle sort order">
          <mat-icon>{{ sortDir['pending'] === 'desc' ? 'arrow_downward' : 'arrow_upward' }}</mat-icon>
        </button>
      </div>
      <div class="lane-body">
        <app-class-card
          *ngFor="let cls of visiblePending; trackBy: trackById"
          [cls]="cls"
          [canArchive]="canArchive"
          role="listitem"
          (cardClick)="navigateToClass($event)"
          (archiveClick)="archiveClass($event)">
        </app-class-card>
        <div class="empty-state" *ngIf="pendingClasses.length === 0">
          <mat-icon>pending_actions</mat-icon>
          <p>No pending classes</p>
        </div>
        <div class="load-more-row" *ngIf="hasPendingMore">
          <button mat-stroked-button class="load-more-btn" (click)="loadMore('pending')">
            <mat-icon>expand_more</mat-icon> Show more
          </button>
        </div>
      </div>
    </div>

    <!-- Upcoming Lane -->
    <div class="lane" role="list" aria-label="Upcoming classes">
      <div class="lane-header lane-header-upcoming">
        <div class="lane-header-left">
          <span class="lane-title">Upcoming</span>
          <span class="lane-count" [attr.aria-label]="upcomingClasses.length + ' upcoming'">{{ upcomingClasses.length }}</span>
        </div>
        <button mat-icon-button (click)="toggleLaneSort('upcoming')" [matTooltip]="sortDir['upcoming'] === 'desc' ? 'Sort ascending' : 'Sort descending'" aria-label="Toggle sort order">
          <mat-icon>{{ sortDir['upcoming'] === 'desc' ? 'arrow_downward' : 'arrow_upward' }}</mat-icon>
        </button>
      </div>
      <div class="lane-body">
        <app-class-card
          *ngFor="let cls of visibleUpcoming; trackBy: trackById"
          [cls]="cls"
          [canArchive]="canArchive"
          role="listitem"
          (cardClick)="navigateToClass($event)"
          (archiveClick)="archiveClass($event)">
        </app-class-card>
        <div class="empty-state" *ngIf="upcomingClasses.length === 0">
          <mat-icon>upcoming</mat-icon>
          <p>No upcoming classes</p>
        </div>
        <div class="load-more-row" *ngIf="hasUpcomingMore">
          <button mat-stroked-button class="load-more-btn" (click)="loadMore('upcoming')">
            <mat-icon>expand_more</mat-icon> Show more
          </button>
        </div>
      </div>
    </div>

    <!-- Ongoing Lane -->
    <div class="lane" role="list" aria-label="Ongoing classes">
      <div class="lane-header lane-header-ongoing">
        <div class="lane-header-left">
          <span class="lane-title">Ongoing</span>
          <span class="lane-count" [attr.aria-label]="ongoingClasses.length + ' ongoing'">{{ ongoingClasses.length }}</span>
        </div>
        <button mat-icon-button (click)="toggleLaneSort('ongoing')" [matTooltip]="sortDir['ongoing'] === 'desc' ? 'Sort ascending' : 'Sort descending'" aria-label="Toggle sort order">
          <mat-icon>{{ sortDir['ongoing'] === 'desc' ? 'arrow_downward' : 'arrow_upward' }}</mat-icon>
        </button>
      </div>
      <div class="lane-body">
        <app-class-card
          *ngFor="let cls of visibleOngoing; trackBy: trackById"
          [cls]="cls"
          [canArchive]="canArchive"
          role="listitem"
          (cardClick)="navigateToClass($event)"
          (archiveClick)="archiveClass($event)">
        </app-class-card>
        <div class="empty-state" *ngIf="ongoingClasses.length === 0">
          <mat-icon>monitor_heart</mat-icon>
          <p>No ongoing classes</p>
        </div>
        <div class="load-more-row" *ngIf="hasOngoingMore">
          <button mat-stroked-button class="load-more-btn" (click)="loadMore('ongoing')">
            <mat-icon>expand_more</mat-icon> Show more
          </button>
        </div>
      </div>
    </div>

    <!-- Completed Lane -->
    <div class="lane" role="list" aria-label="Completed classes">
      <div class="lane-header lane-header-completed">
        <div class="lane-header-left">
          <span class="lane-title">Completed</span>
          <span class="lane-count" [attr.aria-label]="completedClasses.length + ' completed'">{{ completedClasses.length }}</span>
        </div>
        <button mat-icon-button (click)="toggleLaneSort('completed')" [matTooltip]="sortDir['completed'] === 'desc' ? 'Sort ascending' : 'Sort descending'" aria-label="Toggle sort order">
          <mat-icon>{{ sortDir['completed'] === 'desc' ? 'arrow_downward' : 'arrow_upward' }}</mat-icon>
        </button>
      </div>
      <div class="lane-body">
        <app-class-card
          *ngFor="let cls of visibleCompleted; trackBy: trackById"
          [cls]="cls"
          [canArchive]="canArchive"
          role="listitem"
          (cardClick)="navigateToClass($event)"
          (archiveClick)="archiveClass($event)">
        </app-class-card>
        <div class="empty-state" *ngIf="completedClasses.length === 0">
          <mat-icon>check_circle_outline</mat-icon>
          <p>No completed classes</p>
        </div>
        <div class="load-more-row" *ngIf="hasCompletedMore">
          <button mat-stroked-button class="load-more-btn" (click)="loadMore('completed')">
            <mat-icon>expand_more</mat-icon> Show more
          </button>
        </div>
      </div>
    </div>

  </div><!-- /board -->

  <!-- ── Table View ──────────────────────────────────────────────── -->
  <div class="cv2-table-wrapper" *ngIf="!isLoading && viewMode === 'table'">
    <div class="cv2-table-container">
      <table mat-table [dataSource]="tableDataSource" matSort class="cv2-table" aria-label="Classes table">

        <!-- Name -->
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Name</th>
          <td mat-cell *matCellDef="let cls" class="col-name">{{ cls.name }}</td>
        </ng-container>

        <!-- Course / Template -->
        <ng-container matColumnDef="course">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Course / Template</th>
          <td mat-cell *matCellDef="let cls">{{ getCourseName(cls) }}</td>
        </ng-container>

        <!-- Trainers -->
        <ng-container matColumnDef="trainers">
          <th mat-header-cell *matHeaderCellDef>Trainer(s)</th>
          <td mat-cell *matCellDef="let cls" class="col-trainers">{{ getTrainerNames(cls) }}</td>
        </ng-container>

        <!-- Start date -->
        <ng-container matColumnDef="startDate">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Start Date</th>
          <td mat-cell *matCellDef="let cls">{{ formatDate(getStartDate(cls)) }}</td>
        </ng-container>

        <!-- End date -->
        <ng-container matColumnDef="endDate">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>End Date</th>
          <td mat-cell *matCellDef="let cls">{{ formatDate(getEndDate(cls)) }}</td>
        </ng-container>

        <!-- Status -->
        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
          <td mat-cell *matCellDef="let cls">
            <span class="status-chip" [ngClass]="getStatusCssClass(cls)">{{ getStatusLabel(cls) }}</span>
          </td>
        </ng-container>

        <!-- Stage -->
        <ng-container matColumnDef="stage">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Stage</th>
          <td mat-cell *matCellDef="let cls">{{ getStage(cls) }}</td>
        </ng-container>

        <!-- Actions -->
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef></th>
          <td mat-cell *matCellDef="let cls" (click)="$event.stopPropagation()">
            <button *ngIf="canArchive" mat-icon-button matTooltip="Archive this class" (click)="archiveClass(cls.id)">
              <mat-icon>inventory_2</mat-icon>
            </button>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="tableColumns; sticky: true"></tr>
        <tr
          mat-row
          *matRowDef="let cls; columns: tableColumns;"
          class="table-row"
          (click)="navigateToClass(cls.id)"
          tabindex="0"
          (keydown.enter)="navigateToClass(cls.id)"
          [attr.aria-label]="'Open class: ' + cls.name">
        </tr>

        <!-- No-data row -->
        <tr class="mat-row" *matNoDataRow>
          <td class="mat-cell no-data-cell" [attr.colspan]="tableColumns.length">
            No classes match your search.
          </td>
        </tr>
      </table>
    </div>
    <mat-paginator
      [pageSizeOptions]="pageSizeOptions"
      [pageSize]="pageSize"
      appPersistPageSize="classes:list"
      showFirstLastButtons
      aria-label="Page through classes">
    </mat-paginator>
  </div>

</div>
